import React from "react";
import { motion } from "framer-motion";
import { SectionHeading } from "../SectionHeading";
import { skillGroups } from "../../data/portfolio";
const captions: Record<string, string> = {
  "CREATE & CAPTURE": "Art Direction & concept",
  BUILD: "3D & CGI craft",
  MOTION: "Animation",
  COMPOSITE: "VFX & finishing",
  ACCELERATE: "AI-assisted pipeline",
  DELIVER: "Edit & handover"
};

/** Flip to true to bring back the single-view pipeline summary above the discipline cards. */
const SHOW_PIPELINE_SUMMARY = false;
export function SkillsSection() {
  const total = skillGroups.reduce((count, group) => count + group.skills.length, 0);
  return <section id="skills" aria-labelledby="skills-heading" className="border-y border-white/10 bg-[#070b0b] px-5 py-24 md:px-8 md:py-32" data-id="element-252" data-mp="198d95jbxmukq">
      <div className="mx-auto max-w-[1480px]" data-id="element-253" data-mp="3b2ce7mygofoz">
        <SectionHeading index="006" label="Expertise, disciplines, tools" titleStart="Creative judgement," accent="technical" titleEnd="precision." headingId="skills-heading" body="Every discipline below is used in production, grouped by where it lands in the pipeline rather than listed as one long keyword cloud." data-id="element-254" data-mp="3b3nirozqdrh5" />

        {/* The whole pipeline in a single view, before the discipline detail below. */}
        {SHOW_PIPELINE_SUMMARY && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-8%"
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-14 overflow-hidden rounded-[20px] border border-white/10 bg-[#0a0f0e]" data-id="element-255" data-mp="393iqmlz6nvla">
          <div className="flex items-center justify-between gap-4 px-6 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45 md:px-8" data-id="element-256" data-mp="3rwmko8gbcagf">
            <span className="flex items-center gap-3" data-id="element-257" data-mp="2ldobfxre5uxv">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#54f6c6] dot-blink" data-id="element-258" data-mp="2ggen6lfqnpbm" />
              Pipeline at a glance
            </span>
            <span data-id="element-259" data-mp="18fefd6lgdufb">{total} disciplines</span>
          </div>

          <ol className="grid gap-px border-t border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-id="element-260" data-mp="1chj71zsccgzb">
            {skillGroups.map((group, index) => <li key={group.label} className="group/stage relative bg-[#0a0f0e] px-6 py-6 transition-colors duration-500 hover:bg-[#0d1413] md:px-6" data-id="element-261" data-mp="13hmjk1hoto5u" data-mpi={index}>
                <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px scale-x-0 bg-[#54f6c6]/70 transition-transform duration-500 group-hover/stage:scale-x-100" data-id="element-262" data-mp="tkhuntwgjelc" />
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/35" data-id="element-263" data-mp="ksg1mgopx0l3">
                  {String(index + 1).padStart(2, "0")}
                </p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-264" data-mp="au0awetlrtc4">{group.label}</p>
                <p className="mt-2 text-sm leading-6 text-white/70" data-id="element-265" data-mp="3ne4936uxqx5i">{captions[group.label] ?? ""}</p>
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-white/40" data-id="element-266" data-mp="3jcizbudp6yzj">
                  {String(group.skills.length).padStart(2, "0")} skills
                </p>
              </li>)}
          </ol>
        </motion.div>}

        <div className={`grid gap-5 sm:grid-cols-2 lg:grid-cols-3 ${SHOW_PIPELINE_SUMMARY ? "mt-5" : "mt-16"}`} data-id="element-267" data-mp="a617bpafp9si">
          {skillGroups.map((group, index) => <motion.article key={group.label} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-8%"
        }} transition={{
          duration: 0.6,
          delay: Math.min(index * 0.07, 0.35),
          ease: [0.16, 1, 0.3, 1]
        }} className="group relative overflow-hidden rounded-[20px] border border-white/10 bg-[#0a0f0e] p-6 transition-colors duration-500 hover:border-[#54f6c6]/40 md:p-7" data-id="element-268" data-mp="62v3eoasnup7" data-mpi={index}>
              <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px scale-x-0 bg-gradient-to-r from-transparent via-[#54f6c6]/70 to-transparent transition-transform duration-500 group-hover:scale-x-100" data-id="element-269" data-mp="2icbq77b7y0kx" />

              <div className="flex items-start justify-between" data-id="element-270" data-mp="2x2st6v3i4yjx">
                <div data-id="element-271" data-mp="2x5578x652orp">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-272" data-mp="1zfbnso8ynjx8">{group.label}</p>
                  <p className="mt-2 text-sm text-white/55" data-id="element-273" data-mp="24e54sqnght25">{captions[group.label] ?? ""}</p>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45" data-id="element-274" data-mp="29d86c1489h5d">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>

              <ul className="mt-7 flex flex-wrap gap-2" data-id="element-275" data-mp="3ktg2nkt2fw8l">
                {group.skills.map((skill, skillIndex) => <motion.li key={skill} initial={{
              opacity: 0,
              y: 10,
              scale: 0.96
            }} whileInView={{
              opacity: 1,
              y: 0,
              scale: 1
            }} viewport={{
              once: true,
              margin: "-6%"
            }} transition={{
              duration: 0.45,
              delay: 0.1 + skillIndex * 0.05,
              ease: [0.16, 1, 0.3, 1]
            }} className="group/tag inline-flex cursor-default items-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-2 text-sm text-white/85 transition-[transform,background-color,border-color,color] duration-300 hover:-translate-y-0.5 hover:-rotate-1 hover:border-[#54f6c6] hover:bg-[#54f6c6] hover:text-[#05100d]" data-id="element-276" data-mp="3pqetnuhfnn2k" data-mpi={skillIndex}>
                    <span aria-hidden="true" className="h-1 w-1 shrink-0 rounded-full bg-[#54f6c6] transition-colors duration-300 group-hover/tag:bg-[#05100d]" data-id="element-277" data-mp="lhtaxr9qcknn" />
                    {skill}
                  </motion.li>)}
              </ul>

            </motion.article>)}
        </div>

      </div>
    </section>;
}